import { useId } from 'react'
import { cn } from '../../../lib/cn'
import { Explicacao } from '../../../components/Explicacao'
import { TEXTOS } from '../../../lib/glossario'
import type { ResumoGmud, StatusGmud } from '../../../types/gmud'
import { ETAPA_COM_DIVERGENCIA, proximaAcaoDaEtapa, RESPONSAVEL_NA_TELA } from '../detalhe/etapasDoAnexoXxi'
import type { LigacoesDoArraste } from './arraste'
import { idDaColuna, quantasNaColuna, type ColunaDoQuadro as Coluna } from './colunasDoQuadro'
import { QUADRO } from './textosDoQuadro'
import { ZonaDeStatus } from './ZonaDeStatus'

interface Props {
  coluna: Coluna
  porStatus: Map<StatusGmud, ResumoGmud[]>
  ligacoes: LigacoesDoArraste
}

/**
 * Uma etapa do ANEXO XXI, com quem responde por ela e o que está parado ali.
 *
 * O cabeçalho responde "de quem estamos esperando" para a coluna inteira — a pergunta que trava
 * uma solicitação por dias e que a etiqueta de estado, sozinha, nunca respondeu. Título, resumo e
 * responsável vêm de `ETAPAS`: o anexo é transcrito uma vez só, em `detalhe/etapasDoAnexoXxi.ts`.
 *
 * **A etapa 7 carrega a marca de divergência.** O ANEXO XXI atribui o fechamento à CONTRATADA e
 * neste sistema quem encerra é a CONTRATANTE (decisão nº 4 do dono, ainda em aberto). Sem a marca,
 * o cabeçalho desta coluna afirmaria uma atribuição que o sistema não cumpre — e quem encontraria
 * a divergência depois seria a contratada, numa contestação.
 *
 * @param props a coluna, o agrupamento por estado e os fios do arraste
 * @returns a coluna
 */
export function ColunaDoQuadro({ coluna, porStatus, ligacoes }: Props) {
  const { etapa } = coluna
  const proximaAcao = proximaAcaoDaEtapa(etapa.numero)
  const idDoTitulo = useId()
  const quantas = quantasNaColuna(porStatus, coluna)

  return (
    // Coluna vazia mais estreita: com as sete na mesma largura, quem abria o quadro via quatro
    // colunas vazias e as solicitações paradas ficavam fora da tela, à direita. Continua alvo de
    // arraste — só ocupa menos espaço enquanto não tem o que mostrar.
    //
    // A largura é BASE, e a coluna cresce: num monitor de 1920px as sete cabem lado a lado (a
    // sétima, "Fechamento", ficava atrás da rolagem com larguras fixas); em tela menor a base
    // segura a coluna e a faixa rola, como antes.
    <li className={cn('shrink-0 grow transition-[flex-basis]', quantas === 0 ? 'basis-[11.5rem]' : 'basis-72')}>
      {/* `tabIndex={-1}` para a barra de salto poder trazer o foco até aqui: sem isso o salto move
          a rolagem e deixa o foco onde estava, e quem navega por teclado continua na barra. */}
      <section
        id={idDaColuna(etapa.numero)}
        tabIndex={-1}
        aria-labelledby={idDoTitulo}
        className="flex h-full flex-col rounded-xl border border-slate-200 bg-slate-100/80 p-2"
      >
        <header className="px-1 pb-2">
          <h3 id={idDoTitulo} className="text-sm font-semibold text-slate-900">
            {etapa.numero}. {etapa.titulo}
          </h3>
          <p className="mt-0.5 flex items-center gap-1 text-xs font-medium text-slate-700">
            {proximaAcao
              ? QUADRO.responsavelDaColuna(RESPONSAVEL_NA_TELA[proximaAcao])
              : QUADRO.responsavelContratual(RESPONSAVEL_NA_TELA[etapa.responsavel])}
            {etapa.numero === ETAPA_COM_DIVERGENCIA && (
              <Explicacao
                titulo={etapa.titulo}
                texto={TEXTOS.etapas.divergenciaDaEtapa7}
                className="h-4 w-4 text-amber-700"
              />
            )}
          </p>
          <p className="text-xs text-slate-500">{QUADRO.contagem(quantas)}</p>
        </header>

        {quantas === 0 && <p className="px-1 pb-1 text-xs text-slate-500">{QUADRO.colunaVazia}</p>}

        <div className="space-y-1">
          {coluna.zonas.map((zona) => (
            <ZonaDeStatus
              key={zona.status}
              zona={zona}
              itens={porStatus.get(zona.status) ?? []}
              rotuloDaColuna={etapa.titulo}
              ligacoes={ligacoes}
            />
          ))}
        </div>
      </section>
    </li>
  )
}
